export const RODDO_AGE_GATE_PASSED_KEY = 'roddo_age_gate_13_plus_v1';
export const RODDO_AGE_GATE_BLOCKED_KEY = 'roddo_age_gate_under13_v1';
export const RODDO_AGE_BRACKET_KEY = 'roddo_age_bracket_v1';
export const RODDO_AGE_POLICY_VERSION = 'age-eligibility-v1';

export type RoddoAgeBracket = 'U13' | '13_15' | '16_17' | '18_PLUS';
export type RoddoEligibleAgeBracket = Exclude<RoddoAgeBracket, 'U13'>;

export type RoddoAgeCheck = {
  valid: boolean;
  bracket?: RoddoAgeBracket;
  age?: number;
  reason?: 'missing' | 'invalid_month' | 'invalid_year';
};

export type RoddoAgeEligibilityRecord = {
  verified: true;
  bracket: RoddoEligibleAgeBracket;
  verifiedAt: number;
  policyVersion: string;
};

/**
 * Neutral month/year age screen.
 *
 * We intentionally do not require a full birth date. Because the day of month
 * is unknown, the calculation is conservative during the user's birth month:
 * the next age is not credited until the following month. That avoids treating
 * someone as 13 before their actual 13th birthday.
 */
export function checkRoddoAgeEligibility(
  birthMonthInput: string | number,
  birthYearInput: string | number,
  now = new Date(),
): RoddoAgeCheck {
  const birthMonth = Number(birthMonthInput);
  const birthYear = Number(birthYearInput);

  if (!birthMonthInput || !birthYearInput) return { valid: false, reason: 'missing' };
  if (!Number.isInteger(birthMonth) || birthMonth < 1 || birthMonth > 12) {
    return { valid: false, reason: 'invalid_month' };
  }

  const currentYear = now.getFullYear();
  if (!Number.isInteger(birthYear) || birthYear < currentYear - 120 || birthYear > currentYear) {
    return { valid: false, reason: 'invalid_year' };
  }

  const currentMonth = now.getMonth() + 1;
  let age = currentYear - birthYear;

  // With no day-of-month, wait until the birth month is fully past before
  // crediting the next year of age.
  if (currentMonth <= birthMonth) age -= 1;

  const bracket: RoddoAgeBracket =
    age < 13 ? 'U13' : age < 16 ? '13_15' : age < 18 ? '16_17' : '18_PLUS';

  return { valid: true, bracket, age };
}

export function isRoddoTeenBracket(bracket?: RoddoAgeBracket | null): boolean {
  return bracket === '13_15' || bracket === '16_17';
}

export function isRoddoEligibleAgeBracket(value: unknown): value is RoddoEligibleAgeBracket {
  return value === '13_15' || value === '16_17' || value === '18_PLUS';
}

export function buildRoddoAgeEligibilityRecord(
  bracket: RoddoEligibleAgeBracket,
  verifiedAt = Date.now(),
): RoddoAgeEligibilityRecord {
  return {
    verified: true,
    bracket,
    verifiedAt,
    policyVersion: RODDO_AGE_POLICY_VERSION,
  };
}

/**
 * Account-level age eligibility is authoritative once a user profile is hydrated.
 * Legacy top-level/legal fields remain readable so existing users do not need to
 * repeat the age check just because the storage shape was upgraded.
 */
export function getRoddoVerifiedAgeBracket(profile: any): RoddoEligibleAgeBracket | null {
  const record = profile?.ageEligibility;
  const recordBracket = record?.bracket;
  if (
    record?.verified === true &&
    isRoddoEligibleAgeBracket(recordBracket)
  ) {
    return recordBracket;
  }

  const legacyBracket = profile?.ageBracket ?? profile?.legal?.minor?.ageBracket;
  const legacyVerifiedAt = Number(
    profile?.ageVerifiedAt ?? profile?.ageEligibility?.verifiedAt ?? 0,
  );

  if (isRoddoEligibleAgeBracket(legacyBracket) && legacyVerifiedAt > 0) {
    return legacyBracket;
  }

  // Current legal acceptance already records the bracket server-side. Preserve
  // that as a migration fallback for users accepted before ageEligibility existed.
  if (
    isRoddoEligibleAgeBracket(profile?.legal?.minor?.ageBracket) &&
    profile?.legal?.tos?.acceptedAt &&
    profile?.legal?.privacy?.acceptedAt
  ) {
    return profile.legal.minor.ageBracket;
  }

  return null;
}
